import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api, formatApiError, useAuth } from '@/App';
import PdfPreviewDialog from '@/components/PdfPreviewDialog';
import NotesThread from '@/components/NotesThread';
import { 
  ArrowLeft, 
  Buildings, 
  Lightning, 
  Drop,
  Clock,
  FileText,
  User,
  CalendarCheck,
  CheckCircle,
  Warning,
  Upload,
  Eye,
  Download,
  Trash
} from '@phosphor-icons/react';

const INTERNAL_STATUSES = ['New Service Request', 'Quotation Accepted', 'Site Visit Scheduled',
                           'Assigned to Agent', 'Query Logged', 'Under Review', 'Approved', 'Completed'];

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

const STATUS_COLORS = {
  'New Service Request': 'bg-blue-100 text-blue-700',
  'Quotation Accepted': 'bg-indigo-100 text-indigo-700',
  'Site Visit Scheduled': 'bg-cyan-100 text-cyan-700',
  'Assigned to Agent': 'bg-gray-100 text-gray-700',
  'Query Logged': 'bg-yellow-100 text-yellow-700',
  'Under Review': 'bg-orange-100 text-orange-700',
  'Approved': 'bg-green-100 text-green-700',
  'Completed': 'bg-emerald-100 text-emerald-700'
};

const AdminCaseDetail = () => {
  const { caseId } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const fetchCase = async () => {
    try {
      const { data } = await api.get(`/cases/${caseId}`);
      setCaseData(data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load case');
      navigate('/admin/cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, navigate]);

  const handleUpdate = async (updates) => {
    setUpdating(true);
    try {
      const { data } = await api.put(`/cases/${caseId}`, updates);
      setCaseData(prev => ({ ...prev, ...data }));
      toast.success('Case updated');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUpdating(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Please choose a file'); return; }
    const label = (uploadType || uploadFile.name).trim();
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('files', uploadFile);
      await api.post(
        `/service-requests/${caseId}/documents?document_types=${encodeURIComponent(label)}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Document uploaded — visible to client');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadType('');
      await fetchCase();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Delete this document? Clients will no longer see it.')) return;
    setDeletingDocId(docId);
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Document removed');
      await fetchCase();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingDocId(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Case Details">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  const handleDelete = async () => {
    if (!window.confirm('Archive this query?\n\nIt will be hidden from the case list and the client portal. Quotations and invoices are retained for 5 years and it can be restored from the Archived view.')) return;
    try {
      await api.post(`/cases/${caseId}/archive`, { reason: 'Archived from case detail' });
      toast.success('Query archived');
      navigate('/admin/cases');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleRestore = async () => {
    try {
      await api.post(`/cases/${caseId}/restore`);
      toast.success('Query restored');
      fetchCase();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!caseData) return null;

  const statusIndex = INTERNAL_STATUSES.indexOf(caseData.internal_status);

  return (
    <AdminLayout title="Case Details">
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/cases">
              <Button variant="ghost" size="icon" className="rounded-none">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#0055FF]/10 flex items-center justify-center">
                {caseData.service_type?.includes('Water') ? (
                  <Drop className="w-6 h-6 text-[#0055FF]" />
                ) : (
                  <Lightning className="w-6 h-6 text-[#0055FF]" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#0A192F]">{caseData.service_type}</h2>
                <p className="text-sm text-[#4B5563] font-mono">{caseData.case_id}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={STATUS_COLORS[caseData.internal_status] || 'bg-gray-100'}>
              {caseData.internal_status}
            </Badge>
            <Badge className={caseData.priority === 'Urgent' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
              {caseData.priority}
            </Badge>
            {!caseData.archived_at && (
              <Button variant="outline" size="sm" className="rounded-none text-red-600 border-red-300 hover:bg-red-50" onClick={handleDelete} data-testid="delete-case-btn">
                Delete
              </Button>
            )}
          </div>
        </div>

        {caseData.archived_at && (
          <div className="flex items-center justify-between gap-4 border border-amber-300 bg-amber-50 px-4 py-3" data-testid="case-archived-banner">
            <div>
              <p className="text-sm font-semibold text-amber-900">This query is archived</p>
              <p className="text-xs text-amber-800 mt-0.5">
                Hidden from the case list and client portal. {caseData.archive_reason ? `Reason: ${caseData.archive_reason}. ` : ''}
                Financial records retained until {caseData.retention_until ? new Date(caseData.retention_until).toLocaleDateString() : 'the retention period ends'}.
              </p>
            </div>
            <Button size="sm" className="rounded-none bg-[#0055FF] hover:bg-[#0044CC]" onClick={handleRestore} data-testid="restore-case-btn">
              Restore Query
            </Button>
          </div>
        )}

        {/* Progress Timeline */}
        <Card>
          <CardHeader className="border-b border-[#E5E7EB] pb-4">
            <CardTitle className="text-lg font-semibold text-[#0A192F]">Progress</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between overflow-x-auto pb-4">
              {INTERNAL_STATUSES.map((status, index) => (
                <div key={status} className="flex flex-col items-center min-w-[100px]">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    index < statusIndex ? 'bg-[#10B981] text-white' :
                    index === statusIndex ? 'bg-[#0055FF] text-white' :
                    'bg-[#E5E7EB] text-[#4B5563]'
                  }`}>
                    {index < statusIndex ? <CheckCircle className="w-4 h-4" weight="fill" /> : index + 1}
                  </div>
                  <p className={`text-xs mt-2 text-center ${index <= statusIndex ? 'text-[#0A192F] font-medium' : 'text-[#4B5563]'}`}>
                    {status}
                  </p>
                  {index < INTERNAL_STATUSES.length - 1 && (
                    <div className={`absolute w-full h-0.5 ${index < statusIndex ? 'bg-[#10B981]' : 'bg-[#E5E7EB]'}`} style={{ top: '16px', left: '50%' }}></div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company & Meter */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4">
                <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                  <Buildings className="w-5 h-5 text-[#0055FF]" />
                  Company Details
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {caseData.company && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#4B5563]">Company</p>
                        <Link to={`/admin/companies/${caseData.company.company_id}`} className="text-[#0055FF] font-medium hover:underline">
                          {caseData.company.company_name}
                        </Link>
                      </div>
                      <Badge className={`lead-${caseData.company.lead_status?.toLowerCase().replace(' ', '-')}`}>
                        {caseData.company.lead_status}
                      </Badge>
                    </div>
                    {caseData.company.physical_address && (
                      <div>
                        <p className="text-sm text-[#4B5563]">Address</p>
                        <p className="text-[#0A192F]">{caseData.company.physical_address}</p>
                      </div>
                    )}
                    {caseData.company.company_phone && (
                      <div>
                        <p className="text-sm text-[#4B5563]">Phone</p>
                        <p className="text-[#0A192F]">{caseData.company.company_phone}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {caseData.meter && (
                  <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
                    <p className="text-sm font-semibold text-[#0A192F] mb-4">Linked Meter</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-[#4B5563]">Meter Number</p>
                        <p className="text-[#0A192F] font-mono">{caseData.meter.meter_number}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#4B5563]">Type</p>
                        <p className="text-[#0A192F]">{caseData.meter.meter_type}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#4B5563]">Payment Type</p>
                        <p className="text-[#0A192F]">{caseData.meter.payment_type}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#4B5563]">Supplier</p>
                        <p className="text-[#0A192F]">{caseData.meter.energy_supplier}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Internal Notes thread (staff only) */}
            <NotesThread
              caseId={caseId}
              notes={caseData.internal_notes_thread || []}
              legacyNote={caseData.internal_notes}
              onPosted={fetchCase}
              onUpdated={fetchCase}
              internal
              currentUser={currentUser}
            />

            {/* Public Notes / Client Conversation thread */}
            <NotesThread
              caseId={caseId}
              notes={caseData.notes_thread || []}
              legacyNote={caseData.public_notes}
              onPosted={fetchCase}
              onUpdated={fetchCase}
              currentUser={currentUser}
            />

            {/* Timeline */}
            {caseData.timeline && caseData.timeline.length > 0 && (
              <Card>
                <CardHeader className="border-b border-[#E5E7EB] pb-4">
                  <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#0055FF]" />
                    Activity Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="relative pl-8 space-y-6">
                    {caseData.timeline.map((activity, index) => (
                      <div key={index} className="timeline-item relative">
                        <p className="text-sm font-medium text-[#0A192F]">{activity.action}</p>
                        <p className="text-xs text-[#4B5563] mt-1">
                          {new Date(activity.timestamp).toLocaleString()}
                        </p>
                        {activity.details && Object.keys(activity.details).length > 0 && (
                          <p className="text-sm text-[#4B5563] mt-2">
                            {JSON.stringify(activity.details)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4">
                <CardTitle className="text-lg font-semibold text-[#0A192F]">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-[#0A192F] mb-2 block">Status</Label>
                  <Select 
                    value={caseData.internal_status} 
                    onValueChange={(v) => handleUpdate({ internal_status: v })}
                  >
                    <SelectTrigger className="rounded-none" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INTERNAL_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#0A192F] mb-2 block">Priority</Label>
                  <Select 
                    value={caseData.priority} 
                    onValueChange={(v) => handleUpdate({ priority: v })}
                  >
                    <SelectTrigger className="rounded-none" data-testid="priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Case Info */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4">
                <CardTitle className="text-lg font-semibold text-[#0A192F]">Case Info</CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center gap-3">
                  <CalendarCheck className="w-5 h-5 text-[#4B5563]" />
                  <div>
                    <p className="text-xs text-[#4B5563]">Date Logged</p>
                    <p className="text-sm text-[#0A192F]">{new Date(caseData.date_logged || caseData.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                {caseData.target_completion_date && (
                  <div className="flex items-center gap-3">
                    <Warning className="w-5 h-5 text-[#F59E0B]" />
                    <div>
                      <p className="text-xs text-[#4B5563]">Target Date</p>
                      <p className="text-sm text-[#0A192F]">{new Date(caseData.target_completion_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-[#4B5563]" />
                  <div>
                    <p className="text-xs text-[#4B5563]">Client Status</p>
                    <Badge className="mt-1">{caseData.public_status}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Documents */}
            <Card>
              <CardHeader className="border-b border-[#E5E7EB] pb-4 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-lg font-semibold text-[#0A192F]">Documents</CardTitle>
                <Button
                  size="sm"
                  className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none"
                  onClick={() => setUploadOpen(true)}
                  data-testid="admin-upload-doc-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Upload
                </Button>
              </CardHeader>
              <CardContent className="pt-6">
                {caseData.documents && caseData.documents.length > 0 ? (
                  <div className="space-y-2">
                    {caseData.documents.map((doc) => (
                      <div key={doc.doc_id} className="flex items-center justify-between p-3 border border-[#E5E7EB]" data-testid={`doc-row-${doc.doc_id}`}>
                        <div className="flex-1 min-w-0 pr-4">
                          <p className="text-sm font-medium text-[#0A192F] truncate">{doc.original_filename}</p>
                          {doc.document_type && doc.document_type !== doc.original_filename && (
                            <p className="text-xs text-gray-500">{doc.document_type}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-[#0055FF]"
                            onClick={() => setPreview({ url: `/api/documents/${doc.doc_id}/download`, title: doc.document_type || 'Document', filename: doc.original_filename })}
                            data-testid={`view-doc-${doc.doc_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                          <a href={`/api/documents/${doc.doc_id}/download`} target="_blank" rel="noopener noreferrer" download={doc.original_filename}>
                            <Button variant="ghost" size="sm" data-testid={`download-doc-${doc.doc_id}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                          </a>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            disabled={deletingDocId === doc.doc_id}
                            onClick={() => handleDeleteDoc(doc.doc_id)}
                            data-testid={`delete-doc-${doc.doc_id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#4B5563] text-center py-4">No documents uploaded</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={uploadOpen} onOpenChange={(open) => !uploading && setUploadOpen(open)}>
        <DialogContent className="sm:max-w-[500px]" data-testid="admin-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload Document to Case</DialogTitle>
            <DialogDescription>
              Uploaded documents are visible to the client on their case page. Allowed: PDF, JPG, PNG (max 10MB).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-semibold">Document Label (optional)</Label>
              <Input
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                placeholder="e.g. Application Form, Approval Letter"
                className="rounded-none mt-1"
                data-testid="admin-upload-label"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">File *</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="rounded-none mt-1"
                data-testid="admin-upload-file"
              />
              {uploadFile && (
                <p className="text-xs text-gray-500 mt-1">
                  {uploadFile.name} ({(uploadFile.size / 1024).toFixed(0)} KB)
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setUploadOpen(false)} className="rounded-none" disabled={uploading}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || !uploadFile}
                className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none"
                data-testid="admin-upload-submit"
              >
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog
        open={!!preview}
        onOpenChange={(open) => !open && setPreview(null)}
        url={preview?.url}
        title={preview?.title}
        filename={preview?.filename}
      />
    </AdminLayout>
  );
};

export default AdminCaseDetail;
